// src/services/axios-config.ts

import axios, { type AxiosInstance } from "axios";
import { getRuntimeConfig } from "../config/runtime-config";
import {
	addApiRequestBreadcrumb,
	addApiResponseBreadcrumb,
	isAxiosError,
	shouldTrackError,
	trackApiError,
	trackAuthenticationError,
	trackAuthorizationError,
	trackNetworkError,
	trackServerError,
} from "../utils/api-error-tracking";

export interface CreateAxiosInstanceOptions {
	authToken: string;
	baseURL?: string;
	timeout?: number;
	apiVersion?: string;
	walletAddress?: string;
	transactionId?: string;
}

/**
 * Get the service path - all API versions use rampsservice
 */
const getServicePath = (): string => {
	return "rampsservice";
};

// Cache for axios instances to avoid creating multiple instances with same config
const instanceCache = new Map<string, AxiosInstance>();

// Generate a cache key based on configuration
const getCacheKey = (
	baseURL: string,
	timeout: number,
	authToken: string,
	apiVersion: string,
	walletAddress?: string,
	transactionId?: string,
): string => {
	// Use first 8 chars of token for cache key (or hash it)
	const tokenKey = authToken ? authToken.slice(0, 8) : "no-token";
	const walletKey = walletAddress ? `:${walletAddress.slice(0, 8)}` : "";
	const txKey = transactionId ? `:${transactionId.slice(0, 8)}` : "";
	return `${baseURL}:${timeout}:${tokenKey}:${apiVersion}${walletKey}${txKey}`;
};

export const createAuthenticatedAxios = ({
	authToken,
	baseURL,
	timeout,
	apiVersion,
	walletAddress,
	transactionId,
}: CreateAxiosInstanceOptions): AxiosInstance => {
	// Get runtime configuration
	const config = getRuntimeConfig();

	// Use provided baseURL, otherwise use runtime config
	const finalBaseURL = baseURL || config.apiUrl;
	const finalTimeout = timeout || config.timeout || 30000;
	const finalApiVersion = apiVersion || config.apiVersion || "v3";

	const cacheKey = getCacheKey(
		finalBaseURL,
		finalTimeout,
		authToken,
		finalApiVersion,
		walletAddress,
		transactionId,
	);
	const cachedInstance = instanceCache.get(cacheKey);

	if (cachedInstance) {
		// No need to update interceptors - they're already correct for this token
		return cachedInstance;
	}

	// Debug logging for API configuration
	if (import.meta.env.MODE === "development" || config.enableDebugLogs) {
		// Creating new Axios instance
	}

	// Get service path - all versions use rampsservice
	const servicePath = getServicePath();
	const baseURLWithVersion = `${finalBaseURL}/${servicePath}/${finalApiVersion}`;

	const instance = axios.create({
		baseURL: baseURLWithVersion,
		timeout: finalTimeout,
		headers: {
			"Content-Type": "application/json",
		},
	});

	// Setup interceptors
	setupRequestInterceptor(
		instance,
		authToken,
		finalApiVersion,
		walletAddress,
		transactionId,
	);
	setupResponseInterceptor(instance);

	// Cache the instance
	instanceCache.set(cacheKey, instance);

	return instance;
};

// Separate function to setup request interceptor (for reuse with cached instances)
const setupRequestInterceptor = (
	instance: AxiosInstance,
	authToken: string,
	apiVersion: string,
	walletAddress?: string,
	transactionId?: string,
): void => {
	instance.interceptors.request.use(
		(config) => {
			config.headers.Authorization = `Bearer ${authToken}`;
			// Removed X-Request-ID header per user request

			// For V5 API, add walletAddress as query parameter and X-MG-TransactionId header
			if (apiVersion === "v5") {
				if (walletAddress) {
					config.params = {
						...config.params,
						walletAddress,
					};
				}

				// Add X-MG-TransactionId header for v5 API
				if (transactionId) {
					config.headers["X-MG-TransactionId"] = transactionId;
					if (import.meta.env.MODE === "development") {
						console.log(
							`✅ Added X-MG-TransactionId header: ${transactionId} for ${config.url}`,
						);
					}
				} else if (import.meta.env.MODE === "development") {
					console.warn(
						`⚠️ No transactionId available for v5 request: ${config.url}`,
					);
				}
			}

			// Add Sentry breadcrumb for API request
			if (config.url) {
				addApiRequestBreadcrumb(
					config.method || "GET",
					config.url,
					config.data as Record<string, unknown>,
				);
			}

			// Debug logging for Ramps service API calls
			if (
				import.meta.env.MODE === "development" &&
				config.url?.includes("rampsservice")
			) {
				// Ramps API Request
			}

			return config;
		},
		(error) => {
			if (import.meta.env.MODE === "development") {
				console.error("🔥 Request interceptor error:", error);
			}

			// Track request setup errors
			if (isAxiosError(error)) {
				trackNetworkError(error);
			}

			return Promise.reject(error);
		},
	);
};

// Separate function to setup response interceptor (only needed once per instance)
const setupResponseInterceptor = (instance: AxiosInstance): void => {
	instance.interceptors.response.use(
		(response) => {
			// Add Sentry breadcrumb for successful response
			if (response.config.url) {
				addApiResponseBreadcrumb(
					response.config.method || "GET",
					response.config.url,
					response.status,
				);
			}

			// Debug logging for successful Ramps service API responses
			if (
				import.meta.env.MODE === "development" &&
				response.config.url?.includes("rampsservice")
			) {
				// Ramps API Response
			}
			return response;
		},
		(error) => {
			// Enhanced error logging for debugging PostHog interference
			if (import.meta.env.MODE === "development") {
				console.error("🔥 Stellar API Error Details:", {
					url: error.config?.url,
					method: error.config?.method,
					status: error.response?.status,
					statusText: error.response?.statusText,
					data: error.response?.data,
					message: error.message,
					isNetworkError: !error.response,
					isTimeout: error.code === "ECONNABORTED",
				});
			}

			// Track API errors with Sentry based on error type
			if (isAxiosError(error) && shouldTrackError(error.response?.status)) {
				const statusCode = error.response?.status;

				// Track specific error types
				if (statusCode === 401) {
					trackAuthenticationError(error);
				} else if (statusCode === 403) {
					trackAuthorizationError(error);
				} else if (statusCode && statusCode >= 500) {
					trackServerError(error);
				} else if (!error.response) {
					// Network error (no response)
					trackNetworkError(error);
				} else {
					// Other errors (400-499 except 401/403)
					trackApiError(error, {
						tags: {
							service: error.config?.url?.includes("rampsservice")
								? "ramps"
								: "api",
						},
					});
				}
			}

			// Special handling for quote API - if we get a 500 but have valid response data, don't throw
			if (
				error.response?.status === 500 &&
				error.config?.url?.includes("/transactions/quote") &&
				error.response?.data &&
				typeof error.response.data === "object"
			) {
				// Check if the response contains valid quote structure
				const data = error.response.data;
				if (data.transactions || data.senderProfile || data.errors) {
					// Return the response data even though status is 500
					// This allows the quote service to process the data and handle errors appropriately
					return {
						...error.response,
						status: 200, // Override status to prevent further error handling
					};
				}
			}

			// Handle specific error cases
			if (error.response?.status === 401) {
				// Token expired or invalid
				throw new Error("Session expired. Please refresh.");
			}

			if (error.response?.status === 403) {
				throw new Error("Access denied");
			}

			if (error.response?.status >= 500) {
				throw new Error("Server error. Please try again.");
			}

			// Return formatted error
			const message =
				error.response?.data?.message || error.message || "Network error";
			throw new Error(message);
		},
	);
};

/**
 * Clear the axios instance cache
 * Useful for testing or when you need to force recreation of instances
 */
export const clearAxiosCache = (): void => {
	instanceCache.clear();
};

/**
 * Get cache statistics for debugging
 */
export const getAxiosCacheStats = () => {
	return {
		size: instanceCache.size,
		keys: Array.from(instanceCache.keys()),
	};
};
